// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState } from 'react';
import { login } from '../api/auth';
import { brandingApi } from '../api/brandingApi';
import type { BrandingConfig } from '../types/branding';

const EyeOpenIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeClosedIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [branding, setBranding] = useState<BrandingConfig | null>(null);
  const [brandingLoaded, setBrandingLoaded] = useState(false);

  useEffect(() => {
    const providerCode = window.location.hostname.split('.')[0];
    brandingApi.getPublicBranding(providerCode)
      .then((b) => setBranding(b))
      .catch(() => setBranding(null))
      .finally(() => setBrandingLoaded(true));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setError('Please enter your email and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const data = await login(email, password);

      if (data.authenticated === true) {
        window.location.href = '/';
      } else {
        switch (data.error) {
          case 'invalid_credentials':
            setError('Invalid email or password');
            break;
          case 'account_disabled':
            setError('Your account has been deactivated. Contact your administrator.');
            break;
          case 'account_locked':
            setError('Your account is temporarily locked due to too many failed attempts. Please try again later.');
            break;
          default:
            setError(data.errorDescription || 'Login failed. Please try again.');
        }
      }
    } catch {
      setError('Unable to connect to the authentication service. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-4">
      <div className="w-full max-w-5xl flex rounded-[40px] overflow-hidden shadow-lg min-h-[600px]">

        {/* Left decorative panel */}
        <div
          className="hidden md:flex md:w-[40%] flex-col justify-between p-10 relative"
          style={{
            background: 'linear-gradient(227deg, #C250B0 0%, #542D6E 100%)',
          }}
          aria-hidden="true"
        >
          <div className="flex-1 flex items-center">
            <p className="text-white text-3xl font-semibold leading-snug">
              Secure, Instant, and Ready to Share with your Advisor.
            </p>
          </div>

          <div className="inline-flex items-center gap-1.5 bg-white rounded-full px-3 py-1.5 self-start">
            <span className="text-xs font-medium text-gray-700">Powered by Finerty</span>
          </div>
        </div>

        {/* Right form panel */}
        <div className="flex-1 flex flex-col justify-center px-10 py-12 bg-white">
          <div className="max-w-form w-full mx-auto">

            {/* Logo */}
            <div className="mb-8">
              {!brandingLoaded ? (
                <div className="h-10 w-32 bg-gray-100 rounded animate-pulse" aria-hidden="true" />
              ) : branding?.logoUrl ? (
                <img
                  src={branding.logoUrl}
                  alt="Provider logo"
                  className="h-10 object-contain"
                />
              ) : branding?.displayName ? (
                <p className="h-10 flex items-center text-xl font-semibold text-gray-900">
                  {branding.displayName}
                </p>
              ) : null}
            </div>

            {/* Heading */}
            <h1
              className="font-medium text-[#141414] mb-2"
              style={{ fontSize: '40px', lineHeight: '1.2' }}
            >
              Welcome Back 👋
            </h1>
            <p className="text-[#333] mb-8" style={{ fontSize: '20px' }}>
              Today is a new day. It&apos;s your day. You shape it.
            </p>

            {/* Form */}
            <form onSubmit={handleSubmit} noValidate>
              <div className="flex flex-col gap-5">

                {/* Email field */}
                <div className="flex flex-col gap-1.5">
                  <label
                    className="text-sm font-medium text-[#141414]"
                    htmlFor="email"
                  >
                    Email address<span className="text-red-500 ml-0.5">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(''); }}
                    disabled={loading}
                    autoFocus
                    autoComplete="username"
                    className="w-full h-12 border border-[#525252] rounded-lg px-4 text-sm text-[#141414] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2D0A6C] focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>

                {/* Password field */}
                <div className="flex flex-col gap-1.5">
                  <label
                    className="text-sm font-medium text-[#141414]"
                    htmlFor="password"
                  >
                    Password<span className="text-red-500 ml-0.5">*</span>
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Password"
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setError(''); }}
                      disabled={loading}
                      autoComplete="current-password"
                      className="w-full h-12 border border-[#525252] rounded-lg px-4 pr-11 text-sm text-[#141414] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2D0A6C] focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <EyeClosedIcon /> : <EyeOpenIcon />}
                    </button>
                  </div>
                </div>

                {/* Error message */}
                {error && (
                  <div
                    role="alert"
                    className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3"
                  >
                    {error}
                  </div>
                )}

                {/* Submit button */}
                <button
                  type="submit"
                  disabled={loading}
                  aria-busy={loading}
                  className="w-full h-12 bg-[#2D0A6C] text-white text-sm font-semibold rounded-xl hover:bg-[#3d1290] focus:outline-none focus:ring-2 focus:ring-[#2D0A6C] focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Logging in...' : 'Log In'}
                </button>

                {/* Terms */}
                <p className="text-xs text-center text-[#333]">
                  By logging in you agree to the{' '}
                  <span className="underline cursor-pointer">Terms &amp; Conditions</span>
                  {' '}and{' '}
                  <span className="underline cursor-pointer">Privacy Policy</span>
                </p>

              </div>
            </form>
          </div>
        </div>

      </div>
    </div>
  );
};
